import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { CheckCircle2, Download, FileSpreadsheet, Loader2, Lock, LockOpen, Paperclip, Plus, Undo2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/partners`;

const eur = (v) => `${(v ?? 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const currentMonth = () => new Date().toISOString().slice(0, 7);

const W_STATUS = {
  draft: { label: 'Brouillon', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
  validated: { label: 'Validé', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  cancelled: { label: 'Annulé', cls: 'bg-red-50 text-red-700 border-red-200' },
};
const TYPES = [
  { key: 'prelevement', label: 'Prélèvement' },
  { key: 'remboursement', label: 'Remboursement' },
  { key: 'avance', label: 'Avance' },
  { key: 'regularisation', label: 'Régularisation' },
];

const BalancesCard = ({ balances }) => (
  <Card className="border-border" data-testid="partner-balances-card">
    <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
      <CardTitle className="text-base">Répartition — {balances.period}</CardTitle>
      <div className="flex items-center gap-3">
        <span className="text-sm text-muted-foreground">Total encaissé :</span>
        <span className="text-xl font-bold" data-testid="partner-total-collected">{eur(balances.total_collected)}</span>
        <Badge variant="outline" className={balances.status === 'cloture' ? 'text-red-700 border-red-300 bg-red-50' : 'text-amber-700 border-amber-300 bg-amber-50'} data-testid="partner-period-status">
          {balances.status === 'cloture' ? 'Clôturée' : 'Brouillon'}
        </Badge>
      </div>
    </CardHeader>
    <CardContent>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Associé</TableHead>
            <TableHead className="text-right">Quote-part</TableHead>
            <TableHead className="text-right">Part théorique</TableHead>
            <TableHead className="text-right">Déjà prélevé</TableHead>
            <TableHead className="text-right">Solde dû</TableHead>
            <TableHead className="text-right">Trop-perçu</TableHead>
            <TableHead className="text-right">Solde cumulé</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {balances.partners.map((p) => (
            <TableRow key={p.partner_key} data-testid={`partner-balance-${p.partner_key}`}>
              <TableCell className="font-medium">{p.partner_name}</TableCell>
              <TableCell className="text-right">{(p.share * 100).toFixed(0)} %</TableCell>
              <TableCell className="text-right">{eur(p.theoretical_share)}</TableCell>
              <TableCell className="text-right">{eur(p.taken)}</TableCell>
              <TableCell className={`text-right font-semibold ${p.solde_du > 0 ? 'text-emerald-700' : ''}`} data-testid={`partner-solde-du-${p.partner_key}`}>{eur(p.solde_du)}</TableCell>
              <TableCell className={`text-right font-semibold ${p.trop_percu > 0 ? 'text-red-600' : ''}`} data-testid={`partner-trop-percu-${p.partner_key}`}>{eur(p.trop_percu)}</TableCell>
              <TableCell className={`text-right font-bold ${p.cumulative_balance < 0 ? 'text-red-600' : ''}`} data-testid={`partner-cumul-${p.partner_key}`}>{eur(p.cumulative_balance)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </CardContent>
  </Card>
);

const AddWithdrawalDialog = ({ open, onClose, period, onCreated }) => {
  const [form, setForm] = useState({ partner_key: '', date: new Date().toISOString().slice(0, 10), amount: '', type: 'prelevement', comment: '' });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!form.partner_key || !form.amount) { toast.error('Associé et montant requis'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/withdrawals`, { ...form, amount: parseFloat(form.amount), period });
      toast.success('Mouvement créé (brouillon)');
      setForm({ partner_key: '', date: new Date().toISOString().slice(0, 10), amount: '', type: 'prelevement', comment: '' });
      onCreated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="withdrawal-dialog">
        <DialogHeader>
          <DialogTitle>Nouveau mouvement — période {period}</DialogTitle>
          <DialogDescription>Le mouvement est créé en brouillon puis doit être validé.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="space-y-1">
            <Label>Associé</Label>
            <Select value={form.partner_key} onValueChange={(v) => setForm({ ...form, partner_key: v })}>
              <SelectTrigger data-testid="withdrawal-partner-select"><SelectValue placeholder="Choisir..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="david">David (40 %)</SelectItem>
                <SelectItem value="jerome">Jérôme (30 %)</SelectItem>
                <SelectItem value="sam">Sam (30 %)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Type</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="withdrawal-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Date</Label>
            <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="withdrawal-date-input" />
          </div>
          <div className="space-y-1">
            <Label>Montant (€)</Label>
            <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="withdrawal-amount-input" />
          </div>
          <div className="space-y-1 col-span-2">
            <Label>Commentaire</Label>
            <Textarea rows={2} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} data-testid="withdrawal-comment-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={submit} disabled={busy} data-testid="withdrawal-submit-btn">
            {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Créer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const WithdrawalsCard = ({ period, withdrawals, periodClosed, onChanged }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const act = async (id, action, body = {}) => {
    setBusy(true);
    try {
      await axios.post(`${API}/withdrawals/${id}/${action}`, body);
      toast.success(action === 'validate' ? 'Mouvement validé' : 'Mouvement annulé (écriture inverse si validé)');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setBusy(false); }
  };

  const uploadJustif = async (id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/withdrawals/${id}/justificatif`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Justificatif ajouté');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <Card className="border-border" data-testid="partner-withdrawals-card">
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Mouvements ({withdrawals.length})</CardTitle>
        <Button size="sm" className="gap-1" disabled={periodClosed} onClick={() => setDialogOpen(true)} data-testid="withdrawal-add-btn">
          <Plus className="h-4 w-4" />Nouveau mouvement
        </Button>
      </CardHeader>
      <CardContent>
        {withdrawals.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">Aucun mouvement sur cette période.</p>
        ) : (
          <ul className="divide-y divide-border">
            {withdrawals.map((w) => {
              const s = W_STATUS[w.status] || W_STATUS.draft;
              return (
                <li key={w.id} className="py-2 flex items-center justify-between gap-3 flex-wrap" data-testid={`withdrawal-row-${w.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">
                      {w.partner_name} · {w.type_label} · <span className={w.amount < 0 ? 'text-red-600' : ''}>{eur(w.amount)}</span>
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {w.date}{w.comment && ` · ${w.comment}`}
                      {w.reversal_of && ' · ↩ écriture inverse'}
                      {w.reversed_by && ' · contre-passé'}
                    </p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Badge variant="outline" className={s.cls}>{s.label}</Badge>
                    {w.justificatif_name ? (
                      <a href={`${API}/withdrawals/${w.id}/justificatif`} target="_blank" rel="noreferrer">
                        <Button size="icon" variant="ghost" title="Télécharger le justificatif" data-testid={`withdrawal-justif-dl-${w.id}`}><Download className="h-4 w-4" /></Button>
                      </a>
                    ) : (
                      <label title="Ajouter un justificatif (PDF/JPG/PNG)">
                        <Button size="icon" variant="ghost" asChild><span className="cursor-pointer"><Paperclip className="h-4 w-4" /></span></Button>
                        <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => uploadJustif(w.id, e.target.files[0])} data-testid={`withdrawal-justif-input-${w.id}`} />
                      </label>
                    )}
                    {!periodClosed && w.status === 'draft' && (
                      <Button size="sm" variant="outline" className="gap-1 text-emerald-700" disabled={busy} onClick={() => act(w.id, 'validate')} data-testid={`withdrawal-validate-${w.id}`}>
                        <CheckCircle2 className="h-3.5 w-3.5" />Valider
                      </Button>
                    )}
                    {!periodClosed && (w.status === 'draft' || (w.status === 'validated' && !w.reversed_by && !w.reversal_of)) && (
                      <Button size="sm" variant="ghost" className="gap-1 text-red-600" disabled={busy} onClick={() => act(w.id, 'cancel')} data-testid={`withdrawal-cancel-${w.id}`}>
                        <Undo2 className="h-3.5 w-3.5" />Annuler
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
      <AddWithdrawalDialog open={dialogOpen} onClose={() => setDialogOpen(false)} period={period} onCreated={onChanged} />
    </Card>
  );
};

export default function AdminPartners() {
  const [period, setPeriod] = useState(currentMonth());
  const [balances, setBalances] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [busy, setBusy] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [b, w] = await Promise.all([
        axios.get(`${API}/balances`, { params: { period } }),
        axios.get(`${API}/withdrawals`, { params: { period } }),
      ]);
      setBalances(b.data);
      setWithdrawals(w.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement');
    }
  }, [period]);

  useEffect(() => { setBalances(null); fetchAll(); }, [fetchAll]);

  const togglePeriod = async () => {
    const closed = balances?.status === 'cloture';
    setBusy(true);
    try {
      await axios.post(`${API}/periods/${period}/${closed ? 'reopen' : 'close'}`);
      toast.success(closed ? 'Période rouverte' : 'Période clôturée — soldes figés');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally { setBusy(false); }
  };

  const closed = balances?.status === 'cloture';

  return (
    <AdminLayout title="Compte courant associés" subtitle="Répartition David 40 % · Jérôme 30 % · Sam 30 % — sur encaissements réels">
      <div className="space-y-4" data-testid="admin-partners">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <Input type="month" value={period} onChange={(e) => e.target.value && setPeriod(e.target.value)}
            className="w-44" data-testid="partner-period-input" />
          <div className="flex gap-2 flex-wrap">
            <a href={`${API}/export?period=${period}&format=xlsx`} target="_blank" rel="noreferrer">
              <Button size="sm" variant="outline" className="gap-1" data-testid="partner-export-xlsx"><FileSpreadsheet className="h-4 w-4" />Excel</Button>
            </a>
            <a href={`${API}/export?period=${period}&format=csv`} target="_blank" rel="noreferrer">
              <Button size="sm" variant="outline" className="gap-1" data-testid="partner-export-csv"><Download className="h-4 w-4" />CSV</Button>
            </a>
            {balances && (
              <Button size="sm" variant={closed ? 'outline' : 'default'} className="gap-1" disabled={busy}
                onClick={togglePeriod} data-testid="partner-close-period-btn">
                {closed ? <LockOpen className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                {closed ? 'Rouvrir la période' : 'Clôturer la période'}
              </Button>
            )}
          </div>
        </div>

        {!balances ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <>
            <BalancesCard balances={balances} />
            <WithdrawalsCard period={period} withdrawals={withdrawals} periodClosed={closed} onChanged={fetchAll} />
          </>
        )}
      </div>
    </AdminLayout>
  );
}
